import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { FlexCol, SpinnerContainer } from '@/base-components';
import AddCompanyModal from '@/components/AddCompany/AddCompanyModal';
import { LoginSignup } from '@/components/LoginSignup';
import { SignUpLoginContainer } from '@/components/LoginSignup/styles';
import { useUser } from '@/hooks';
import { useOrganizationContext } from '@/hooks';
import { padding } from '@/theme/variables';
import { getQueryParam, taxonomicalHref } from '@/utils/helpers';
import { safeServerSideTranslations } from '@/utils/i18n';

export const Index = () => {
  const { t } = useTranslation(['common']);
  const router = useRouter();
  const { actions } = useOrganizationContext();
  const hasRedirected = useRef(false);
  const [isAddOrgModalOpen, setIsAddOrgModalOpen] = useState(false);

  const onLoginFinish = async user => {
    const organizations = user?.organizations || [];
    if (!organizations.length) {
      setIsAddOrgModalOpen(true);
      return;
    }

    setIsAddOrgModalOpen(false);

    const defaultOrg = user?.['default-profile']?.organization;
    const orgId = defaultOrg?.id;

    const matchedOrg = organizations.find(
      ORG => ORG.organization.id === defaultOrg?.id
    );
    if (matchedOrg) {
      actions.setOrg({
        updateRoute: false,
        newOrg: matchedOrg.organization,
        makeRequest: true,
      });
    }

    const redirectParam = router.query.redirect;
    const redirectQuery = Array.isArray(redirectParam)
      ? redirectParam[0]
      : redirectParam;
    const orgRedirect = orgId
      ? `/reservations/${orgId}?tab=upcoming`
      : '/reservations';

    const start = Array.isArray(router?.query?.start)
      ? router?.query?.start[0]
      : router?.query?.start;
    const end = Array.isArray(router?.query?.end)
      ? router?.query?.end[0]
      : router?.query?.end;
    const itemUrl = Array.isArray(router?.query?.item_url)
      ? router?.query?.item_url[0]
      : router?.query?.item_url;

    const itemRedirect = itemUrl
      ? start
        ? `${itemUrl}?start=${start}${end ? `&end=${end}` : ''}`
        : itemUrl
      : undefined;

    const redirectTarget =
      redirectQuery && redirectQuery.length > 0 ? redirectQuery : orgRedirect;

    const destination = itemRedirect || redirectTarget;

    if (!user?.['phone-number-verified']) {
      const phoneRedirect = `/account/create/phone?updatePhone=true&redirect=${encodeURIComponent(
        destination
      )}`;

      router.replace(phoneRedirect);
      return;
    }

    const isVerified = user?.['phone-number-verified'];
    if (!isVerified) {
      router.push(
        `/account/create/phone?updatePhone=true&redirect=${encodeURIComponent(
          destination
        )}`
      );
      return;
    }

    if (itemRedirect) {
      router.push(taxonomicalHref, itemRedirect);
    } else {
      router.replace(redirectTarget);
    }
  };

  const { user: contextUser } = useUser();

  useEffect(() => {
    if (!router.isReady) return;
    if (!contextUser?.id) return;
    if (hasRedirected.current) return;

    hasRedirected.current = true;

    onLoginFinish(contextUser);
  }, [contextUser, router.isReady]);

  const onSignupFinish = user => {
    if (user) {
      onLoginFinish(user);
      return;
    }

    const queryParam = getQueryParam(router.query);
    router.push(`/reservations${queryParam}`);
  };

  return (
    <>
      {isAddOrgModalOpen && (
        <AddCompanyModal
          isOpen={isAddOrgModalOpen}
          setIsOpen={setIsAddOrgModalOpen}
          forceOpen
          onSuccess={onLoginFinish}
        />
      )}

      <Head>
        <title>{`Kingbee Vans | ${t('common:login.logIn')}`}</title>
      </Head>
      <FlexCol
        data-test-id="login-page"
        mt={10}
        mx={{
          base: `${padding.topNav.mobileLeft}px`,
          lg: `${padding.topNav.left}px`,
        }}
        alignItems="center"
      >
        {/* Logged in (restored session or just submitted) - keep the form
            hidden while we redirect */}
        {contextUser?.id && !isAddOrgModalOpen ? (
          <SpinnerContainer py={16} data-test-id="login-redirecting" />
        ) : (
          <SignUpLoginContainer>
            <LoginSignup
              loginProps={{ onFinish: onLoginFinish }}
              signupProps={{ onFinish: onSignupFinish }}
            />
          </SignUpLoginContainer>
        )}
      </FlexCol>
    </>
  );
};

export const getServerSideProps = async ({ locale }) => ({
  props: {
    ...(await safeServerSideTranslations(locale, [
      'common',
      'account',
      'search',
      'org_menu',
      'reservations',
    ])),
  },
});

export default Index;
